// Global Instructions Rule Applied!

import { useCallback, useEffect, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { fetchFundNoticeSettings } from '../services/fundsService.ts'
import { FundNoticeSettings } from '../../../state_manager/data_models/fund_notice_settings.ts'

/**
 * useFundNoticeSettings — the fund's capital call notice settings (layout,
 * wording overrides, bank account) for the Terms tab notice sections, with a
 * refetch run after every field patch: the layout that applies and the text
 * each wording field shows are resolved server-side.
 *
 * Tab-local detail, like the fund terms: not in activeRecords.
 */
export function useFundNoticeSettings(fundId: string | null | undefined) {
  const recordIdString = fundId ? String(fundId) : null

  const [settings, setSettings] = useState<FundNoticeSettings | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!recordIdString) {
      setSettings(null)
      setLoading(false)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const accessToken = getAccessToken(store.getState().auth)
      const res = await fetchFundNoticeSettings(recordIdString, accessToken)
      if (res.error) setError(res.error)
      if (res.data) setSettings(res.data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load notice settings')
    } finally {
      setLoading(false)
    }
  }, [recordIdString])

  useEffect(() => {
    load()
  }, [load])

  return { settings, loading, error, refetch: load }
}
